/**
 * Der Lauf einer Videoaufnahme — Zeit, Pausen, Grenze.
 *
 * Reine Rechnung, ohne Kamera und ohne Bühne. Das ist der Teil, an dem sich am ehesten etwas
 * verzählt: die Grenze gilt für die **Summe** aller Teilstücke, nicht je Teilstück, und die
 * Zahl der Teilstücke ist eine Angabe, die später im Protokoll steht.
 *
 * ⚠️ **Die Uhr kommt von aussen.** Jede Methode bekommt den Zeitpunkt gereicht, statt selbst
 * `Date.now()` zu rufen — sonst liesse sich eine Aufnahme über fünf Minuten nur prüfen, indem
 * man fünf Minuten wartet.
 */

export type Laufzustand = 'bereit' | 'laeuft' | 'pause' | 'fertig';

export class Aufnahmelauf {
  private zustandIntern: Laufzustand = 'bereit';
  /** Was vor der laufenden Strecke schon zusammengekommen ist. */
  private gesammeltMs = 0;
  /** Beginn der laufenden Strecke, oder `null`, wenn gerade nichts läuft. */
  private seit: number | null = null;
  private stuecke = 0;

  constructor(private readonly maxMs: number) {}

  get zustand(): Laufzustand {
    return this.zustandIntern;
  }

  /**
   * Aus wie vielen Teilstücken die Aufnahme besteht — je Pause eines mehr.
   *
   * Vor dem Start sind es **null**: eine Aufnahme, die nie lief, hat kein Teilstück. Nativ
   * zählt `VideoSegmentMerger` dasselbe, nur dass er sie hinterher wirklich zusammenfügt; im
   * Browser trägt `MediaRecorder` mit `pause()`/`resume()` alles in **eine** Datei.
   */
  get segmente(): number {
    return this.stuecke;
  }

  start(t: number): void {
    if (this.zustandIntern !== 'bereit') return;
    this.zustandIntern = 'laeuft';
    this.seit = t;
    this.stuecke = 1;
  }

  pause(t: number): void {
    if (this.zustandIntern !== 'laeuft') return;
    this.gesammeltMs += t - (this.seit ?? t);
    this.seit = null;
    this.zustandIntern = 'pause';
  }

  weiter(t: number): void {
    if (this.zustandIntern !== 'pause') return;
    this.zustandIntern = 'laeuft';
    this.seit = t;
    this.stuecke += 1;
  }

  beende(t: number): void {
    if (this.zustandIntern === 'fertig') return;
    if (this.zustandIntern === 'laeuft') this.gesammeltMs += t - (this.seit ?? t);
    this.seit = null;
    this.zustandIntern = 'fertig';
  }

  /** Wie lang die Aufnahme an dieser Stelle ist — Pausen zählen nicht mit. */
  dauerMs(t: number): number {
    if (this.zustandIntern !== 'laeuft' || this.seit === null) return this.gesammeltMs;
    return this.gesammeltMs + (t - this.seit);
  }

  /** Was noch bleibt. Nie kleiner als null — eine negative Restzeit sagt niemandem etwas. */
  restMs(t: number): number {
    return Math.max(this.maxMs - this.dauerMs(t), 0);
  }

  /** Ist die Grenze erreicht? Dann beendet der Aufrufer von selbst. */
  istVoll(t: number): boolean {
    return this.dauerMs(t) >= this.maxMs;
  }
}
